import { Component, ErrorInfo, ReactNode } from 'react';
import { captureError } from '../../services/errorReporting';
import { browserNavigation } from '../../utils/browserNavigation';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorId: string;
}

export class AdminErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: '',
    };
  }

  static getDerivedStateFromError(): State {
    return {
      hasError: true,
      errorId: `admin-error-${Date.now().toString(36)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureError(error, {
      source: 'react-error-boundary',
      errorId: this.state.errorId,
      componentStack: errorInfo.componentStack,
    });
  }

  private handleReload = () => {
    browserNavigation.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <section
          className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
          role="alert"
          aria-live="assertive"
        >
          <h1 className="text-lg font-semibold text-slate-950">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            The admin console hit an unexpected error. The issue has been
            recorded for review.
          </p>
          <p className="mt-4 text-xs text-slate-500">
            Reference: {this.state.errorId}
          </p>
          <button
            type="button"
            className="mt-5 rounded-md bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
            onClick={this.handleReload}
          >
            Reload
          </button>
        </section>
      </main>
    );
  }
}
